import {computed, inject, Injectable, signal} from '@angular/core';

import {DirectusService, ProjectTimeFrameDirectus} from '@frontend/directus';
import {Step12ProjectCharterService} from '../step-1-2-project-charter.service';
import {ProjectTimeFrame} from './project-time-frame.type';
import {finalize, forkJoin, Observable, of} from 'rxjs';
import {FixedValueService} from '../../../../../../../shared/services/fixed-value.service';
import {map, switchMap} from 'rxjs/operators';
import {ProjectDataService} from '../../../../../../../shared/services/project-data.service';

const NUM_DAYS_GAP_BETWEEN_TIME_FRAMES = 28;
const NUM_DAYS_TIME_FRAME_DURATION = 21;

@Injectable({providedIn: 'root'})
export class ProjectTimeFrameService {
  private readonly COLLECTION_NAME = 'project_time_frame';
  private readonly projectCharterService = inject(Step12ProjectCharterService);
  private readonly directusService = inject(DirectusService);
  private readonly fixedValueService = inject(FixedValueService);
  private readonly projectDataService = inject(ProjectDataService);

  private readonly isLoading = signal(false);
  readonly isInitialDataLoading = computed(() => {
    return this.projectCharterService.isLoading() || this.isLoading();
  });
  readonly mergedTimeFrames = signal<ProjectTimeFrame[]>([]);

  loadTimeFrames(): void {
    this.isLoading.set(true);
    this.loadDataForFixedValues()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (directusTimeFrames: ProjectTimeFrameDirectus[]) => {
          const mappedItems = this.mapToTimeFrameDTOs(directusTimeFrames);
          this.mergedTimeFrames.set(mappedItems);
        },
      });
  }

  private mapToTimeFrameDTOs(directusItems: ProjectTimeFrameDirectus[]): ProjectTimeFrame[] {
    const savedItems = this.projectCharterService.timeFrameData();

    return directusItems.map((directusItem) => {
      const savedItem = savedItems.find((item) => item.directusId === directusItem.id);

      return {
        id: savedItem?.id,
        milestone: savedItem?.milestone,
        startDate: savedItem?.startDate,
        estimatedEndDate: savedItem?.estimatedEndDate,
        stageDescription: savedItem?.stageDescription,
        includedProjectDescription: savedItem?.includedProjectDescription,
        directusId: savedItem ? savedItem.directusId : directusItem.id,
        translations: directusItem.translations,
        tfNumber: directusItem.tfNumber,
        mandatory: directusItem.mandatory,
      };
    });
  }

  private loadDataForFixedValues(): Observable<ProjectTimeFrameDirectus[]> {
    const projectId = this.projectDataService.getProjectId();
    if (projectId === null) {
      console.error('ProjectTimeFrameService: No project ID available');
      return of([]);
    }

    return this.fixedValueService
      .loadOrInitializeFixedValues(projectId, this.COLLECTION_NAME, () =>
        this.directusService.loadProjectTimeFrames(),
      )
      .pipe(
        switchMap((fixedValueIds: string[]) =>
          forkJoin({
            fixedValueIds: of(fixedValueIds),
            allDirectusTimeFrames: this.directusService.loadProjectTimeFrames(),
          }),
        ),
        map(({fixedValueIds, allDirectusTimeFrames}) =>
          this.filterTimeFramesByFixedValueIds(allDirectusTimeFrames, fixedValueIds),
        ),
      );
  }

  public loadProjectStartDate(): Date | null {
    const createdAt = this.projectDataService.getProject()()?.createdAt;
    if (createdAt !== undefined) {
      return new Date(createdAt);
    }
    return null;
  }

  private filterTimeFramesByFixedValueIds(
    allDirectusTimeFrames: ProjectTimeFrameDirectus[],
    fixedValueIds: string[],
  ): ProjectTimeFrameDirectus[] {
    if (fixedValueIds?.length === 0) {
      return allDirectusTimeFrames;
    }
    return allDirectusTimeFrames.filter((criteria) => fixedValueIds.includes(criteria.id));
  }

  suggestTimeFrames = (startDate: Date) => {
    let projectTimeFrames = this.mergedTimeFrames();
    projectTimeFrames = projectTimeFrames.sort((a, b) => {
      const aNum = a.tfNumber ?? Number.MAX_SAFE_INTEGER;
      const bNum = b.tfNumber ?? Number.MAX_SAFE_INTEGER;
      return aNum - bNum;
    });

    let currentStartDate = new Date(startDate);

    const updatedTimeFrame = projectTimeFrames.map((tf, i) => {
      if (i > 0) {
        currentStartDate = new Date(currentStartDate);
        currentStartDate.setDate(currentStartDate.getDate() + NUM_DAYS_GAP_BETWEEN_TIME_FRAMES);
      }

      const endDate = new Date(currentStartDate);
      endDate.setDate(currentStartDate.getDate() + NUM_DAYS_TIME_FRAME_DURATION);

      const result = {
        ...tf,
        startDate: currentStartDate.toISOString(),
        estimatedEndDate: endDate.toISOString(),
        suggested: true,
      };

      currentStartDate = endDate;
      return result;
    });

    this.mergedTimeFrames.set(updatedTimeFrame);
  };

  getNumTotalSections = (): number => {
    return this.mergedTimeFrames().filter((tf) => tf.mandatory).length;
  };

  getNumCompletedSections = (): number => {
    return (
      this.projectCharterService
        .timeFrameData()
        .map((tf) => this.mergedTimeFrames().filter((mt) => mt.directusId === tf.directusId)[0])
        .filter((mt) => mt != undefined)
        .filter((mt) => mt.mandatory == true)?.length || 0
    );
  };
}
